'use client';

import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { useLocale } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MarketingRankingBlockConfig } from '../../../types';
import {
  MARKETING_PC_PRODUCT_IMAGE_AREA_HEIGHT,
  MARKETING_PC_PRODUCT_CONTENT_CLASS_NAME,
  MARKETING_PC_TOP_PRODUCT_IMAGE_AREA_HEIGHT,
  MARKETING_PC_TOP_PRODUCT_CONTENT_CLASS_NAME,
  MarketingRankingProductCard,
} from './MarketingRankingProductCard';
import {
  isMarketingProductSoldOut,
  getMarketingRankingProductCardView,
  getNumberLikeString,
  getStringValue,
  type MarketingProductItem,
} from './marketingAnniversaryUtils';

const MARKETING_RANKING_INITIAL_DISPLAY_COUNT = 8;
const MARKETING_RANKING_US_LOCALE = 'en';
const MARKETING_RANKING_PC_TOP_THREE_HEIGHT =
  'clamp(232px, min(calc(17.1875vw + 56px), calc(7.8125vw + 176px)), 326px)';
const MARKETING_RANKING_PC_OTHER_HEIGHT =
  'clamp(196px, min(calc(14.84375vw + 44px), calc(11.875vw + 82px)), 310px)';

interface MarketingRankingSectionProps {
  rankingBlock: MarketingRankingBlockConfig;
  isWap: boolean;
  cacheState?: MarketingRankingCacheState;
  onCacheStateChange?: (state: MarketingRankingCacheState) => void;
  onTabClick?: (tabName: string, index: number) => void;
  onViewMoreClick?: (tabName: string) => void;
  onProductExposure?: (product: MarketingProductItem, index: number, tabName: string) => void;
  onProductClick?: (product: MarketingProductItem, index: number, tabName: string) => void;
}

interface MarketingRankingCacheState {
  selectedRankingIndex?: number;
  isExpanded?: boolean;
}

export function MarketingRankingSection({
  rankingBlock,
  isWap,
  cacheState,
  onCacheStateChange,
  onTabClick,
  onViewMoreClick,
  onProductExposure,
  onProductClick,
}: MarketingRankingSectionProps) {
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const locale = useLocale();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const [selectedRankingIndex, setSelectedRankingIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasHydratedCacheRef = useRef(false);
  const rankings = useMemo(
    () =>
      rankingBlock.rankings
        .map((ranking) => ({
          ...ranking,
          title: getStringValue(ranking.title) || getStringValue(ranking.tab_title),
          tab_title: getStringValue(ranking.tab_title) || getStringValue(ranking.title),
        }))
        .filter((ranking) => ranking.products.length > 0),
    [rankingBlock.rankings],
  );
  const activeRanking = rankings[selectedRankingIndex] || rankings[0];
  const allProducts = useMemo(
    () =>
      (
        ((activeRanking?.products as Array<Record<string, unknown>> | undefined)?.length
          ? (activeRanking?.products as Array<Record<string, unknown>>)
          : []) || []
      ).map((product) => product as MarketingProductItem),
    [activeRanking?.products],
  );
  const displayCount = MARKETING_RANKING_INITIAL_DISPLAY_COUNT;
  const hasMoreProducts = allProducts.length > displayCount;
  const products = useMemo(
    () => (isExpanded || !hasMoreProducts ? allProducts : allProducts.slice(0, displayCount)),
    [allProducts, displayCount, hasMoreProducts, isExpanded],
  );
  const shouldShowTabs = rankings.length > 1;
  const rankingBackgroundColor = isWap
    ? rankingBlock.wap_background_color || rankingBlock.background_color || '#D9D1FF'
    : rankingBlock.background_color;

  useEffect(() => {
    setSelectedRankingIndex((current) => (current >= rankings.length ? 0 : current));
  }, [rankings.length]);

  useEffect(() => {
    if (hasHydratedCacheRef.current || !cacheState || rankings.length === 0) {
      return;
    }

    const cachedIndex = Number(cacheState.selectedRankingIndex);
    const nextIndex =
      Number.isFinite(cachedIndex) && cachedIndex >= 0 && cachedIndex < rankings.length
        ? cachedIndex
        : 0;

    hasHydratedCacheRef.current = true;
    setSelectedRankingIndex(nextIndex);
    setIsExpanded(Boolean(cacheState.isExpanded));
  }, [cacheState, rankings.length]);

  if (!rankingBlock.visible || rankings.length === 0 || allProducts.length === 0) {
    return null;
  }

  const viewMoreButtonClassName = isWap
    ? 'mx-auto mt-[18px] flex h-[38px] w-[264px] max-w-full cursor-pointer items-center justify-center rounded-[40px] border border-[#282828] bg-transparent font-bb text-[14px] font-bold leading-[18px] text-[#282828]'
    : 'mx-auto mt-[24px] flex h-[48px] w-[340px] min-w-[128px] max-w-full cursor-pointer items-center justify-center rounded-[40px] border border-[#808080] bg-transparent px-[46px] py-[10px] font-bb text-[14px] leading-[18px] text-[#282828]';
  const viewMoreButtonText = tMarketing('common.viewMore');
  const sectionTitle = rankingBlock.title || '';
  const sectionSubTitle = rankingBlock.sub_title || '';
  const renderViewMoreButton =
    hasMoreProducts && !isExpanded ? (
      <button
        type="button"
        className={viewMoreButtonClassName}
        onClick={() => {
          onViewMoreClick?.(activeRanking?.tab_title || '');
          setIsExpanded(true);
          onCacheStateChange?.({
            selectedRankingIndex,
            isExpanded: true,
          });
        }}
      >
        {viewMoreButtonText}
      </button>
    ) : null;
  const renderProductCard = (
    product: MarketingProductItem,
    index: number,
    rank: number,
    isLarge: boolean,
  ) => {
    const cardView = getMarketingRankingProductCardView(
      product,
      currency,
      userInfo,
      activeRanking?.hover_image_rule || rankingBlock.hover_image_rule,
    );
    const shouldShowTopBadge = rank <= 10;
    const ratingText = activeRanking?.show_rating
      ? getNumberLikeString(product.spu_review_stars)
      : '';
    const tabName = activeRanking?.tab_title || '';
    const shouldStackWapPrice = isWap && locale.toLowerCase() !== MARKETING_RANKING_US_LOCALE;

    return (
      <MarketingRankingProductCard
        key={`${getStringValue(product.shop_sku) || getStringValue(product.id) || index}`}
        {...cardView}
        rank={rank}
        rankText={shouldShowTopBadge ? tMarketing('ranking.topRank', { rank }) : ''}
        soldText=""
        ratingText={ratingText || undefined}
        isLarge={isLarge}
        isWap={isWap}
        isSoldOut={isMarketingProductSoldOut(product)}
        soldOutText={tMarketing('flashSale.soldOut')}
        height={
          isWap
            ? undefined
            : isLarge
              ? MARKETING_RANKING_PC_TOP_THREE_HEIGHT
              : MARKETING_RANKING_PC_OTHER_HEIGHT
        }
        imageAreaHeight={
          isWap
            ? undefined
            : isLarge
              ? MARKETING_PC_TOP_PRODUCT_IMAGE_AREA_HEIGHT
              : MARKETING_PC_PRODUCT_IMAGE_AREA_HEIGHT
        }
        imageLayout={isWap ? undefined : isLarge ? 'ranking-top' : 'ranking-normal'}
        contentClassName={
          isWap
            ? undefined
            : isLarge
              ? MARKETING_PC_TOP_PRODUCT_CONTENT_CLASS_NAME
              : MARKETING_PC_PRODUCT_CONTENT_CLASS_NAME
        }
        priceRowClassName={
          isWap
            ? shouldStackWapPrice
              ? 'mt-[14px] flex min-w-0 flex-col items-start gap-y-[2px] font-br'
              : 'mt-[14px] flex min-w-0 flex-wrap items-baseline gap-x-[6px] gap-y-[2px] font-br'
            : undefined
        }
        priceTextClassName={
          isWap
            ? 'shrink-0 whitespace-nowrap font-bb text-[16px] font-bold leading-[16px] text-[#282828]'
            : undefined
        }
        originalPriceTextClassName={
          isWap
            ? 'shrink-0 whitespace-nowrap text-[12px] font-normal leading-normal text-[#808080] line-through'
            : 'text-[12px] font-normal leading-[14px] text-[#808080] line-through'
        }
        onClick={() => onProductClick?.(product, index, tabName)}
        onExposure={() => onProductExposure?.(product, index, tabName)}
      />
    );
  };

  return (
    <section
      className={
        isWap
          ? 'relative z-10 my-[20px] rounded-t-[12px] px-[8px] pb-[40px] pt-[20px]'
          : 'relative z-10 mx-auto my-[20px] w-[calc(100%_-_96px)] max-w-[1680px] min-[1281px]:w-[calc(100%_-_240px)]'
      }
      style={{
        backgroundColor: rankingBackgroundColor,
      }}
    >
      <div className={isWap ? '' : 'pb-[40px] pt-[40px]'}>
        <div className={isWap ? 'mb-[16px] text-center' : 'mb-[24px] text-center'}>
          <h2
            className={`font-bb font-bold text-[#282828] ${
              isWap ? 'text-[20px] leading-[24px]' : 'text-[28px] leading-[34px]'
            }`}
          >
            {shouldShowTabs ? sectionTitle : activeRanking.title || sectionTitle}
          </h2>
          {sectionSubTitle && (
            <p
              className={`mt-[4px] font-br font-normal text-[#808080] ${
                isWap ? 'text-[12px] leading-[14px]' : 'text-[14px] leading-[17px]'
              }`}
            >
              {sectionSubTitle}
            </p>
          )}
        </div>

        {shouldShowTabs && (
          <div
            className={
              isWap
                ? 'relative mb-[16px] flex h-[32px] overflow-hidden rounded-[32px] bg-white/70'
                : 'relative mx-auto mb-[20px] flex h-[48px] w-[min(1036px,100%)] overflow-hidden rounded-[24px] bg-primary-black/10'
            }
          >
            <div
              className={
                isWap
                  ? 'absolute left-0 top-0 h-[32px] rounded-[32px] bg-primary-black transition-transform duration-300 ease-out'
                  : 'absolute left-0 top-0 h-[48px] rounded-[24px] bg-primary-black transition-transform duration-300 ease-out'
              }
              style={
                isWap
                  ? {
                      width: `${100 / rankings.length}%`,
                      transform: `translateX(${selectedRankingIndex * 100}%)`,
                    }
                  : {
                      width: `${100 / rankings.length}%`,
                      transform: `translateX(${selectedRankingIndex * 100}%)`,
                    }
              }
            />
            {rankings.map((ranking, index) => {
              const isActive = index === selectedRankingIndex;
              return (
                <button
                  key={`${ranking.tab_title}-${index}`}
                  type="button"
                  className={
                    isWap
                      ? `relative z-[1] min-w-0 flex-1 cursor-pointer rounded-[32px] px-[8px] text-center text-[13px] capitalize leading-[16px] transition-colors duration-300 ${
                          isActive
                            ? 'h-[32px] font-bb text-white'
                            : 'h-[28px] self-center font-br text-[#282828]'
                        }`
                      : `relative z-[1] h-[48px] min-w-0 flex-1 shrink-0 cursor-pointer rounded-[24px] px-[12px] text-center font-br text-[16px] capitalize leading-[19px] transition-colors duration-300 ${
                          isActive ? 'font-bb text-white' : 'text-[#282828]'
                        }`
                  }
                  onClick={() => {
                    setSelectedRankingIndex(index);
                    setIsExpanded(false);
                    onCacheStateChange?.({
                      selectedRankingIndex: index,
                      isExpanded: false,
                    });
                    onTabClick?.(ranking.tab_title, index);
                  }}
                >
                  <span className="block truncate">{ranking.tab_title}</span>
                </button>
              );
            })}
          </div>
        )}

        {isWap ? (
          <div className="flex flex-col gap-[8px]">
            {products.map((product, index) =>
              renderProductCard(product, index, index + 1, index < 3),
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-[16px]">
            {products.slice(0, 3).length > 0 && (
              <div className="grid grid-cols-3 gap-[16px]">
                {products
                  .slice(0, 3)
                  .map((product, index) => renderProductCard(product, index, index + 1, true))}
              </div>
            )}
            {products.slice(3).length > 0 && (
              <div className="grid grid-cols-5 gap-[16px]">
                {products
                  .slice(3)
                  .map((product, index) => renderProductCard(product, index + 3, index + 4, false))}
              </div>
            )}
          </div>
        )}

        {renderViewMoreButton}
      </div>
    </section>
  );
}
